import { SYNC_STATUS, summarizeSyncRecord, validateSyncPayload } from "./sync-core.mjs";

function validTime(value) {
  return typeof value === "string" && value.length <= 64 && Number.isFinite(Date.parse(value));
}

const CATEGORY_LABELS = {
  network: "网络连接",
  auth: "登录状态",
  service: "云服务状态",
  permission: "云端权限",
  schema: "云端结构",
  conflict: "版本冲突",
  unknown: "未分类错误",
};

const PHASE_LABELS = {
  network: "浏览器网络状态",
  "auth-session": "读取登录状态",
  "auth-sign-in": "提交教师登录",
  "auth-sign-out": "退出教师账号",
  "cloud-read": "读取当前云端版本",
  "cloud-create": "首次建立云端版本",
  "cloud-write": "提交云端新版本",
  "cloud-history": "读取云端历史",
  sync: "同步协调",
};

function withDetails(result, issue) {
  return {
    ...result,
    categoryLabel: issue?.category ? CATEGORY_LABELS[issue.category] ?? CATEGORY_LABELS.unknown : "无错误",
    phaseLabel: issue?.phase ? PHASE_LABELS[issue.phase] ?? "同步请求" : "最近请求已完成",
  };
}

export function classifySyncIssue({ status, error = "", online = true, issue = null } = {}) {
  const message = String(error);
  if (status === SYNC_STATUS.CONFLICT) return withDetails({ code: "conflict", title: "两台设备的数据发生冲突", action: "先导出两个版本，再选择保留哪一份；系统不会自动覆盖。" }, { category: "conflict", phase: "sync" });
  if (!online || status === SYNC_STATUS.OFFLINE_PENDING || /network|fetch|offline|网络不可用|网络连接已断开/i.test(message)) {
    return withDetails({ code: "network", title: "当前网络不可用", action: "本机修改仍然保留；联网后点“重试同步”。" }, issue ?? { category: "network", phase: "network" });
  }
  if (issue?.category === "auth" || /jwt|token|session|auth|登录状态|未登录|401|403/i.test(message)) {
    return withDetails({ code: "auth", title: "登录状态可能已过期", action: "先导出本机 JSON，再退出账号并重新登录。" }, issue ?? { category: "auth", phase: "sync" });
  }
  if (issue?.category === "permission") return withDetails({ code: "permission", title: "云端权限校验未通过", action: "本机数据已保留；请联系管理员核对 RLS、RPC 执行权限和当前账号。" }, issue);
  if (issue?.category === "schema") return withDetails({ code: "schema", title: "云端结构或数据版本不匹配", action: "本机数据已保留；请联系管理员确认三份迁移已按顺序执行。" }, issue);
  if (issue?.category === "service" || /sleep|paused|timeout|timed out|project.*unavailable|503|休眠|暂不可用/i.test(message)) {
    return withDetails({ code: "service", title: "云服务可能正在恢复", action: "免费服务休眠时通常需要等待片刻；不要重复覆盖，稍后点“重试同步”。" }, issue ?? { category: "service", phase: "sync" });
  }
  if (status === SYNC_STATUS.FAILED) return withDetails({ code: "failed", title: "同步未完成", action: "本机数据没有被删除；先重试，仍失败时导出 JSON 备份。" }, issue ?? { category: "unknown", phase: "sync" });
  if (status === SYNC_STATUS.SYNCING) return withDetails({ code: "syncing", title: "正在核对云端版本", action: "请等待状态变为“已同步”后再退出账号。" }, null);
  if (status === SYNC_STATUS.SYNCED) return withDetails({ code: "synced", title: "本机与云端版本一致", action: "最近同步时间和版本号见数据健康页。" }, null);
  return withDetails({ code: "local", title: "当前为本地模式", action: "数据只在本设备浏览器中；请定期导出完整 JSON。" }, null);
}

export function validateHistoryRecord(candidate, expectedOwnerId) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) throw new Error("云端历史记录格式无效");
  if (typeof candidate.owner_id !== "string" || !candidate.owner_id.trim() || candidate.owner_id.length > 256) throw new Error("云端历史记录账号无效");
  if (candidate.owner_id !== expectedOwnerId) throw new Error("云端历史记录不属于当前账号");
  if (!Number.isSafeInteger(candidate.revision) || candidate.revision < 1) throw new Error("云端历史版本号无效");
  if (!validTime(candidate.archived_at)) throw new Error("云端历史时间无效");
  return {
    owner_id: candidate.owner_id,
    revision: candidate.revision,
    archived_at: candidate.archived_at,
    payload: validateSyncPayload(candidate.payload),
  };
}

export function validateHistoryList(candidates, expectedOwnerId) {
  if (!Array.isArray(candidates)) throw new Error("云端历史列表格式无效");
  if (candidates.length > 20) throw new Error("云端历史列表超过 20 个版本");
  const records = candidates.map((item) => validateHistoryRecord(item, expectedOwnerId));
  const revisions = new Set();
  for (const record of records) {
    if (revisions.has(record.revision)) throw new Error("云端历史版本号重复");
    revisions.add(record.revision);
  }
  return records.sort((left, right) => right.revision - left.revision);
}

export function summarizeHistoryRecord(record) {
  const summary = summarizeSyncRecord({ payload: record.payload, revision: record.revision, updated_at: record.archived_at });
  return { ...summary, archivedAt: record.archived_at };
}
